import React, { createContext, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useSessionContext } from 'contexts/session';
import { GET_ADVOCACY_REPRESENTATIVES } from 'hooks/use-get-advocacy-representatives.hook';
import { useUtilityAccountLocation } from 'hooks/use-utility-account-location.hook';
import { LoadingPage } from 'components/loading';
import { ErrorPage } from 'components/errors';
import { GET_ADVOCACY_CAMPAIGN } from './advocacy.api';
import { AdvocacyContext, AdvocacyRepresentatives } from './advocacy.typings';

export const Advocacy = createContext<AdvocacyContext | undefined>(undefined);

interface Props {
  children?: React.ReactNode;
}

export const AdvocacyProvider = ({ children }: Props) => {
  const { userId } = useSessionContext();
  const { utilityAccountId } = useParams<{ utilityAccountId?: string }>();
  const location = useUtilityAccountLocation({
    utilityAccountId: Number(utilityAccountId),
  });
  const [typedAddress, setTypedAddress] = useState<string | undefined>();
  const [representatives, setRepresentatives] = useState<
    AdvocacyRepresentatives | undefined
  >();

  const {
    stateAbbreviation,
    stateFullName,
    displayServiceAddress: address,
  } = location.data || {};

  const repsRequest = useQuery<
    GetAdvocacyRepresentativesQuery,
    GetAdvocacyRepresentativesQueryVariables
  >(GET_ADVOCACY_REPRESENTATIVES, {
    errorPolicy: 'ignore',
    onCompleted: data => {
      const reps = data?.user?.advocacyRepresentatives;
      if (reps) {
        setTypedAddress(address);
        setRepresentatives(reps);
      }
    },
    skip: !address || !!representatives,
    variables: { address: address || '', id: userId },
  });

  const campaignData = useQuery<
    GetAdvocacyCampaignQuery,
    GetAdvocacyCampaignQueryVariables
  >(GET_ADVOCACY_CAMPAIGN, {
    skip: !stateAbbreviation,
    variables: { stateAbbreviation: stateAbbreviation || 'FAKE' },
  });

  const campaign = campaignData.data?.state.activeAdvocacyCampaign;

  const value: AdvocacyContext = useMemo(
    () => ({
      // If the campaign is null or undefined, we handle redirecting back to the solar page in the Advocacy page
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      campaign: campaign!,
      representatives,
      setRepresentatives,
      setTypedAddress,
      stateAbbreviation,
      stateFullName,
      typedAddress,
    }),
    [campaign, representatives, stateAbbreviation, stateFullName, typedAddress]
  );

  if (
    location.loading ||
    campaignData.loading ||
    repsRequest.loading ||
    (repsRequest.data?.user.advocacyRepresentatives && !representatives)
  )
    return <LoadingPage />;

  if (location.error || campaignData.error) return <ErrorPage />;

  return <Advocacy.Provider value={value}>{children}</Advocacy.Provider>;
};
